import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
  clearSavedPosts,
  createCommunityCommentThunk,
  deleteCommunityCommentThunk,
  fetchCommunityCommentsThunk,
  fetchCommunityPostThunk,
  followCommunityAuthorThunk,
  likeCommunityCommentThunk,
  likeCommunityPostThunk,
  toggleSavePost,
  unfollowCommunityAuthorThunk,
  unlikeCommunityCommentThunk,
  unlikeCommunityPostThunk,
} from '../../redux/communitySlice';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import type { CommunityLayoutOutletContext } from '../layout/CommunityLayout';
import CommunityPostCard from '../../components/CommunityPostCard';
import type { AsyncStatus } from '../../redux/communitySlice';
import type { CommunityPost, CommunityTopic } from '../../types';
import './SavedPostsPage.css';

const SavedPostsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { searchTerm } = useOutletContext<CommunityLayoutOutletContext>();
  const navigate = useNavigate();
  const { savedPostIds, savedPosts, comments, topics } = useAppSelector((state) => state.community);
  const { user } = useAppSelector((state) => state.auth);
  const userId = user?.id ?? null;

  const [likePendingMap, setLikePendingMap] = useState<Record<string, boolean>>({});
  const [followPendingMap, setFollowPendingMap] = useState<Record<number, boolean>>({});
  const fetchedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    savedPostIds.forEach((id) => {
      if (!savedPosts[id] && !fetchedRef.current.has(id)) {
        fetchedRef.current.add(id);
        dispatch(fetchCommunityPostThunk(id));
      }
    });
  }, [dispatch, savedPostIds, savedPosts]);

  const postsList = useMemo(() => {
    return savedPostIds
      .map((id) => savedPosts[id])
      .filter((post): post is CommunityPost => Boolean(post));
  }, [savedPostIds, savedPosts]);

  const topicsDict = useMemo(() => {
    return topics.reduce<Record<string, CommunityTopic>>((acc, topic) => {
      acc[topic.id] = topic;
      return acc;
    }, {});
  }, [topics]);

  const filteredPosts = useMemo(() => {
    const keyword = searchTerm.trim().toLowerCase();
    if (!keyword) {
      return postsList;
    }
    return postsList.filter((post) => {
      const titleMatch = (post.title ?? '').toLowerCase().includes(keyword);
      const contentMatch = (post.content ?? '').toLowerCase().includes(keyword);
      const topicMatch = post.topics.some((topicId) => {
        const topic = topicsDict[topicId];
        return topic ? topic.label.toLowerCase().includes(keyword) : false;
      });
      return titleMatch || contentMatch || topicMatch;
    });
  }, [postsList, searchTerm, topicsDict]);

  const handleToggleSave = (post: CommunityPost) => {
    dispatch(toggleSavePost(post));
  };

  const handleToggleLike = async (postId: string, hasLiked: boolean) => {
    if (likePendingMap[postId]) {
      return;
    }
    setLikePendingMap((prev) => ({ ...prev, [postId]: true }));
    try {
      if (hasLiked) {
        await dispatch(unlikeCommunityPostThunk(postId)).unwrap();
      } else {
        await dispatch(likeCommunityPostThunk(postId)).unwrap();
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : '操作失败，请稍后再试';
      window.alert(message);
    } finally {
      setLikePendingMap((prev) => {
        const next = { ...prev };
        delete next[postId];
        return next;
      });
    }
  };

  const handleToggleFollow = async (authorId: number, followsAuthor: boolean) => {
    if (followPendingMap[authorId]) {
      return;
    }
    setFollowPendingMap((prev) => ({ ...prev, [authorId]: true }));
    try {
      if (followsAuthor) {
        await dispatch(unfollowCommunityAuthorThunk(authorId)).unwrap();
      } else {
        await dispatch(followCommunityAuthorThunk(authorId)).unwrap();
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : '操作失败，请稍后再试';
      window.alert(message);
    } finally {
      setFollowPendingMap((prev) => {
        const next = { ...prev };
        delete next[authorId];
        return next;
      });
    }
  };

  const handleLoadComments = (postId: string) => {
    const entry = comments[postId];
    if (!entry || entry.status === 'idle' || entry.status === 'failed') {
      dispatch(fetchCommunityCommentsThunk({ postId }));
    }
  };

  const handleSubmitComment = async (postId: string, content: string, parentCommentId?: string | null) => {
    await dispatch(
      createCommunityCommentThunk({
        postId,
        payload: {
          content,
          parent_comment_id: parentCommentId ?? undefined,
        },
      }),
    ).unwrap();
  };

  const handleDeleteComment = async (postId: string, commentId: string) => {
    await dispatch(deleteCommunityCommentThunk({ postId, commentId })).unwrap();
  };

  const handleToggleCommentLike = async (postId: string, commentId: string, hasLiked: boolean) => {
    const thunk = hasLiked ? unlikeCommunityCommentThunk : likeCommunityCommentThunk;
    await dispatch(thunk({ postId, commentId })).unwrap();
  };

  const handleClearAll = () => {
    if (savedPostIds.length === 0) {
      return;
    }
    const confirmed = window.confirm('确认清空所有收藏的帖子吗？');
    if (!confirmed) {
      return;
    }
    dispatch(clearSavedPosts());
  };

  const handleOpenPost = (postId: string) => {
  navigate(`/app/community/status/${postId}`);
  };

  const handleOpenAuthor = (authorId: number) => {
  navigate(`/app/community/users/${authorId}`);
  };

  if (savedPostIds.length === 0) {
    return (
      <div className="saved-posts">
        <header className="saved-posts__header">
          <h1>我的收藏</h1>
        </header>
        <div className="saved-posts__placeholder">暂无收藏内容，先去逛逛社区吧。</div>
      </div>
    );
  }

  return (
    <div className="saved-posts">
      <header className="saved-posts__header">
        <h1>我的收藏</h1>
        <div className="saved-posts__actions">
          <span>共 {savedPostIds.length} 篇帖子</span>
          <button type="button" onClick={handleClearAll}>
            清空收藏
          </button>
        </div>
      </header>

      {filteredPosts.length === 0 && (
        <div className="saved-posts__placeholder">未找到符合搜索条件的收藏内容。</div>
      )}

      {filteredPosts.map((post) => {
        const commentEntry = comments[post.post_id];
        const commentList = commentEntry?.items ?? [];
        const commentStatus: AsyncStatus = commentEntry?.status ?? 'idle';
        const commentError = commentEntry?.error ?? null;
        const commentCreateStatus: AsyncStatus = commentEntry?.createStatus ?? 'idle';
        const commentCreateError = commentEntry?.createError ?? null;
        const commentLikePending = commentEntry?.likePending ?? {};
        const viewer = post.viewer ?? {};
        const hasSaved = viewer.has_saved ?? true;

        return (
          <CommunityPostCard
            key={post.post_id}
            post={post}
            topicsDict={topicsDict}
            comments={commentList}
            commentsStatus={commentStatus}
            commentsError={commentError}
            createStatus={commentCreateStatus}
            createError={commentCreateError}
            onLoadComments={() => handleLoadComments(post.post_id)}
            onSubmitComment={(content, parentId) => handleSubmitComment(post.post_id, content, parentId)}
            onDeleteComment={(commentId) => handleDeleteComment(post.post_id, commentId)}
            onToggleCommentLike={(commentId, hasLiked) => handleToggleCommentLike(post.post_id, commentId, hasLiked)}
            commentLikePending={commentLikePending}
            currentUserId={userId}
            onToggleLike={() => handleToggleLike(post.post_id, viewer.has_liked ?? false)}
            likePending={likePendingMap[post.post_id]}
            onToggleFollow={post.author?.id ? () => handleToggleFollow(post.author.id, viewer.follows_author ?? false) : undefined}
            followPending={post.author?.id ? followPendingMap[post.author.id] : undefined}
            hasSaved={hasSaved}
            onToggleSave={() => handleToggleSave(post)}
            defaultExpanded={false}
            onOpenPost={() => handleOpenPost(post.post_id)}
            onOpenAuthor={post.author?.id ? () => handleOpenAuthor(post.author.id) : undefined}
          />
        );
      })}
    </div>
  );
};

export default SavedPostsPage;
